import { ChevronDown } from 'lucide-react';
import type { EnumDef } from '@clawhire/shared';
import { enumLabel } from '@/lib/format';
import { cn } from '@/lib/utils';
import { EnumBadge } from '@/components/data/EnumBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface EnumMenuBadgeProps<T extends string> {
  def: EnumDef<T>;
  value: T | string | null | undefined;
  /** Restrict the offered codes (default: all). */
  options?: readonly T[];
  onChange: (value: T) => void;
  /** Render the plain badge (viewer role, erased candidate). */
  readOnly?: boolean;
  /** Menu heading and the accessible name of the trigger ("Pool status"). */
  label: string;
  busy?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/** A status badge that opens a menu to change the value: `<EnumMenuBadge def={PoolStatus} value=… onChange=… />`. */
export function EnumMenuBadge<T extends string>({
  def,
  value,
  options,
  onChange,
  readOnly = false,
  label,
  busy = false,
  size = 'md',
  className,
}: EnumMenuBadgeProps<T>) {
  if (readOnly) return <EnumBadge def={def} value={value} size={size} className={className} />;
  const codes = options ?? def.codes;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring',
            busy && 'opacity-60',
            className,
          )}
          aria-label={`${label}: ${enumLabel(def, value)}. Change`}
          aria-busy={busy || undefined}
          disabled={busy}
        >
          <EnumBadge def={def} value={value} size={size} />
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={typeof value === 'string' ? value : undefined}
          onValueChange={(v) => {
            if (v !== value) onChange(v as T);
          }}
        >
          {codes.map((code) => (
            <DropdownMenuRadioItem key={code} value={code}>
              {def.label(code)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
